import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import './SmartBazaarSale.css';

const OFFER_IMAGE = '/images/promos/smart-bazaar-paisa-vasool-sale.png';
const SALE_START = new Date('2026-08-12T00:00:00+05:30').getTime();
const SALE_END = new Date('2026-08-16T23:59:59+05:30').getTime();

function getRemaining(now) {
  if (now > SALE_END) return { phase: 'ended', ms: 0 };
  if (now < SALE_START) return { phase: 'upcoming', ms: SALE_START - now };
  return { phase: 'live', ms: SALE_END - now };
}

function splitTime(ms) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  return {
    days: String(Math.floor(totalSeconds / 86400)).padStart(2, '0'),
    hours: String(Math.floor((totalSeconds % 86400) / 3600)).padStart(2, '0'),
    minutes: String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0'),
    seconds: String(totalSeconds % 60).padStart(2, '0'),
  };
}

function SmartBazaarSale() {
  const [now, setNow] = useState(() => Date.now());
  const [open, setOpen] = useState(false);
  const remaining = useMemo(() => getRemaining(now), [now]);
  const parts = useMemo(() => splitTime(remaining.ms), [remaining.ms]);

  useEffect(() => {
    if (remaining.phase === 'ended') return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [remaining.phase]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (remaining.phase === 'ended') return null;

  const statusLabel = remaining.phase === 'live' ? 'Ends in' : 'Starts in';
  const tagline =
    remaining.phase === 'live'
      ? 'Happening now at Smart Bazaar'
      : 'Coming soon at Smart Bazaar';

  const ui = (
    <>
      <button
        type="button"
        className="offer-sticky"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${tagline}. ${statusLabel}. Open sale poster`}
      >
        <span className="offer-sticky__text">
          <span className="offer-sticky__eyebrow">Full Paisa Vasool</span>
          <span className="offer-sticky__tagline">{tagline}</span>
        </span>

        <span className="offer-sticky__timer" aria-hidden="true">
          <span className="offer-sticky__status">{statusLabel}</span>
          <span className="offer-sticky__units">
            <span>
              <strong>{parts.days}</strong>
              <small>d</small>
            </span>
            <span>
              <strong>{parts.hours}</strong>
              <small>h</small>
            </span>
            <span>
              <strong>{parts.minutes}</strong>
              <small>m</small>
            </span>
            <span>
              <strong>{parts.seconds}</strong>
              <small>s</small>
            </span>
          </span>
        </span>
      </button>

      {open ? (
        <div
          className="offer-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Smart Bazaar Full Paisa Vasool Sale"
          onClick={() => setOpen(false)}
        >
          <div
            className="offer-modal__card"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="offer-modal__close"
              aria-label="Close offer"
              onClick={() => setOpen(false)}
            >
              ×
            </button>
            <img
              src={OFFER_IMAGE}
              alt="Smart Bazaar Full Paisa Vasool Sale, 12th to 16th August"
              width="720"
              height="960"
            />
            <div className="offer-modal__meta">
              <p className="offer-modal__title">Full Paisa Vasool Sale</p>
              <p className="offer-modal__line">Smart Bazaar · 12th–16th August</p>
              <p className="offer-modal__line">Amma Chekku wood-pressed oils in store</p>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );

  return createPortal(ui, document.body);
}

export default SmartBazaarSale;
